import { useState, useMemo, useCallback, memo, useEffect, Fragment } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { Position, Order } from '../types/mexc';
import { useKlineData } from '../hooks/useMexcApi';
import { useCurrentPrices } from '../hooks/useCurrentPrices';
// import { usePriceRefresh } from '../hooks/usePriceRefresh'; // Unused, commented out
import { usePricePolling } from '../hooks/usePricePolling';
import { PriceRefreshTimer } from './PriceRefreshTimer';
import { transformKlineDataWithIndicators } from '../utils/technicalIndicators';
import { useUpdatePositionTPSL } from '../hooks/useMexcApi';
import {
  BarChart3,
  RefreshCw,
  Target,
  Shield,
  Edit3
} from 'lucide-react';

// Timeframe options for the chart
const TIMEFRAMES = [
  { label: '1m', value: 'Min1', minutes: 1 },
  { label: '5m', value: 'Min5', minutes: 5 },
  { label: '15m', value: 'Min15', minutes: 15 },
  { label: '1h', value: 'Min60', minutes: 60 },
  { label: '4h', value: 'Hour4', minutes: 240 },
  { label: '1d', value: 'Day1', minutes: 1440 },
] as const;

interface TradingChartProps {
  symbol: string;
  positions: Position[];
  orders?: Order[];
  height?: number;
}

interface CandlestickData {
  timestamp: number;
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  amount: number;
}

const TradingChartComponent = ({ symbol, positions, orders = [], height = 400 }: TradingChartProps) => {
  const UI_DEBUG = import.meta.env.DEV && localStorage.getItem('debug_ui') === 'true';
  if (UI_DEBUG) console.log('🎯 TradingChart component rendered with props:', { symbol, positionsCount: positions.length, height });

  const [selectedTimeframe, setSelectedTimeframe] = useState<typeof TIMEFRAMES[number]>(TIMEFRAMES[2]); // Default to 15m
  const [showVolume, setShowVolume] = useState(true);
  const [showIndicators, setShowIndicators] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);

  // Fetch kline data for the selected timeframe
  if (UI_DEBUG) console.log('🎯 TradingChart useKlineData params:', { symbol, timeframe: selectedTimeframe.value });
  const { data: klineData, isLoading, error, refetch } = useKlineData(
    symbol,
    selectedTimeframe.value
  );
  if (UI_DEBUG) console.log('🎯 TradingChart kline hook result:', { klineData, isLoading, error });

  // Manual test function for kline API (DISABLED to reduce API calls)
  const testKlineAPI = async () => {
    // Disabled to prevent excessive API calls
    // This was causing frequent kline API calls shown in console logs
    console.log('🧪 Manual kline API test disabled to reduce API call frequency');
    return;

    /* COMMENTED OUT TO REDUCE API CALLS
    console.log('🧪 Manual kline API test starting...');
    try {
      const mexcApi = (await import('../services/mexcApi')).getMexcApiClient();
      const testSymbol = symbol || 'SOL_USDT';
      const testInterval = selectedTimeframe.value;
      console.log('🧪 Testing with:', { testSymbol, testInterval });

      const result = await mexcApi.getKlineData(testSymbol, testInterval);
      console.log('🧪 Manual kline API test result:', result);

      // Also test the transformation
      if (result && result.time && result.time.length > 0) {
        console.log('🧪 Testing data transformation...');
        const transformed = (await import('../utils/technicalIndicators')).transformKlineDataWithIndicators(result, selectedTimeframe);
        console.log('🧪 Transformation result:', transformed);
      }
    } catch (error) {
      console.error('🧪 Manual kline API test error:', error);
    }
    */
  };

  // Test on component mount (DISABLED to reduce API calls)
  useEffect(() => {
    if (symbol && UI_DEBUG) {
      console.log('🎯 TradingChart mounted with symbol:', symbol);
    }
  }, [symbol]);

  // Get current price data
  const { getCurrentPrice } = useCurrentPrices();
  const currentPriceData = getCurrentPrice(symbol);

  // Get price refresh state for timer (removed unused refreshState)

  // Price polling integration (replaces WebSocket)
  const { lastPollTime } = usePricePolling();
  const isDataFresh = lastPollTime > 0 && (Date.now() - lastPollTime) < 10 * 60 * 1000; // Data is fresh if polled within 10 min

  // Mutation for updating position SL/TP
  const updatePositionTPSL = useUpdatePositionTPSL();

  // Transform kline data into chart format with technical indicators
  const { chartData, indicators } = useMemo(() => {
    if (UI_DEBUG) console.log('🔍 Kline Data Debug:', {
      klineData,
      hasData: !!klineData,
      timeLength: klineData?.time?.length,
      openLength: klineData?.open?.length,
      dataKeys: klineData ? Object.keys(klineData) : 'no data'
    });

    if (!klineData || !klineData.time || !klineData.time.length) {
      console.log('❌ No kline data available for chart');
      return {
        chartData: [],
        indicators: {
          sma20: [],
          sma50: [],
          ema20: [],
          rsi: [],
          supportResistance: [],
          bollingerBands: { upper: [], middle: [], lower: [] }
        }
      };
    }

    if (UI_DEBUG) console.log('✅ Processing kline data with', klineData.time.length, 'data points');
    const transformed = transformKlineDataWithIndicators(klineData, selectedTimeframe);

    // Add indicator values directly to chartData for Recharts compatibility
    const chartDataWithIndicators = transformed.chartData.map((item, index) => ({
      ...item,
      sma20: transformed.indicators.sma20[index] ?? null,
      sma50: transformed.indicators.sma50[index] ?? null,
      ema20: transformed.indicators.ema20[index] ?? null,
      bbUpper: transformed.indicators.bollingerBands.upper[index] ?? null,
      bbLower: transformed.indicators.bollingerBands.lower[index] ?? null,
      rsi: transformed.indicators.rsi[index] ?? null,
    }));

    return {
      chartData: chartDataWithIndicators,
      indicators: transformed.indicators
    };
  }, [klineData, selectedTimeframe]);

  // Calculate position levels for overlays
  const positionLevels = useMemo(() => {
    return positions.map(position => ({
      positionId: position.positionId,
      entryPrice: position.openAvgPrice,
      positionType: position.positionType,
      size: position.holdVol,
      // Calculate unrealized P&L if current price is available
      unrealizedPnL: currentPriceData ?
        (position.positionType === 1 ?
          (currentPriceData.price - position.openAvgPrice) * position.holdVol :
          (position.openAvgPrice - currentPriceData.price) * position.holdVol
        ) : 0
    }));
  }, [positions, currentPriceData]);

  // Get stop-loss and take-profit levels from orders
  const orderLevels = useMemo(() => {
    return orders
      .filter(order => order.stopLossPrice || order.takeProfitPrice)
      .map(order => ({
        orderId: order.orderId,
        stopLoss: order.stopLossPrice,
        takeProfit: order.takeProfitPrice,
        positionId: order.positionId
      }));
  }, [orders]);

  const handleTimeframeChange = useCallback((timeframe: typeof TIMEFRAMES[number]) => {
    setSelectedTimeframe(timeframe);
  }, []);

  const handleRefreshChart = useCallback(() => {
    refetch();
  }, [refetch]);

  // Quick action handlers
  const handleQuickStopLoss = useCallback((position: Position, price: number) => {
    updatePositionTPSL.mutate({
      positionId: position.positionId,
      stopLossPrice: price
    }, {
      onSuccess: () => {
        console.log(`Stop loss updated to $${price.toFixed(4)} for position ${position.positionId}`);
      },
      onError: (error) => {
        console.error('Failed to update stop loss:', error);
      }
    });
  }, [updatePositionTPSL]);

  const handleQuickTakeProfit = useCallback((position: Position, price: number) => {
    updatePositionTPSL.mutate({
      positionId: position.positionId,
      takeProfitPrice: price
    }, {
      onSuccess: () => {
        console.log(`Take profit updated to $${price.toFixed(4)} for position ${position.positionId}`);
      },
      onError: (error) => {
        console.error('Failed to update take profit:', error);
      }
    });
  }, [updatePositionTPSL]);

  const handlePositionSelect = useCallback((position: Position) => {
    setSelectedPosition(position);
    setShowQuickActions(true);
  }, []);

  // Custom tooltip for candlestick data
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as CandlestickData;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">{label}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Open:</span>
              <span className="font-mono">${data.open.toFixed(4)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">High:</span>
              <span className="font-mono text-green-600">${data.high.toFixed(4)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Low:</span>
              <span className="font-mono text-red-600">${data.low.toFixed(4)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Close:</span>
              <span className="font-mono">${data.close.toFixed(4)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Volume:</span>
              <span className="font-mono">{data.volume.toLocaleString()}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {symbol} Chart
            </h3>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isDataFresh ? 'bg-green-500' : 'bg-gray-400'
                }`} />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {isDataFresh ? 'Data Fresh' : 'Loading...'}
              </span>
            </div>
          </div>
        </div>
        <div
          className="flex items-center justify-center"
          style={{ height: `${height}px` }}
        >
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Loading chart data...</p>
            <p className="text-xs text-gray-400 mt-2">
              Fetching {selectedTimeframe.label} data for {symbol}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {symbol} Chart
          </h3>
        </div>
        <div
          className="flex items-center justify-center"
          style={{ height: `${height}px` }}
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-red-500" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Chart Error
            </h4>
            <p className="text-gray-500 dark:text-gray-400 max-w-sm mb-4">
              Failed to load chart data. Please try refreshing.
            </p>
            <button
              onClick={handleRefreshChart}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!chartData.length) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {symbol} Chart
          </h3>
        </div>
        <div
          className="flex items-center justify-center"
          style={{ height: `${height}px` }}
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Chart Data
            </h4>
            <p className="text-gray-500 dark:text-gray-400 max-w-sm mb-4">
              No chart data available for {symbol} at {selectedTimeframe.label} timeframe.
            </p>
            <div className="text-xs text-gray-400 space-y-1">
              <div>Symbol: {symbol || 'undefined'}</div>
              <div>Timeframe: {selectedTimeframe.value}</div>
              <div>Loading: {isLoading ? 'true' : 'false'}</div>
              <div>Error: {error ? 'true' : 'false'}</div>
              <div>Kline Data: {klineData ? 'exists' : 'null'}</div>
              <div>Time Length: {klineData?.time?.length || 0}</div>
              <div>Open Length: {klineData?.open?.length || 0}</div>
            </div>
            <button
              onClick={testKlineAPI}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Test Kline API
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Chart Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {symbol} Chart
            </h3>

            {/* Current Price Display */}
            {currentPriceData && (
              <div className="flex items-center space-x-2">
                <span className="text-2xl font-mono font-bold text-gray-900 dark:text-white">
                  ${currentPriceData.price.toFixed(4)}
                </span>
                <span className={`text-sm px-2 py-1 rounded ${currentPriceData.ticker?.riseFallRate && currentPriceData.ticker.riseFallRate > 0
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                  : currentPriceData.ticker?.riseFallRate && currentPriceData.ticker.riseFallRate < 0
                    ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}>
                  {currentPriceData.ticker?.riseFallRate ?
                    `${currentPriceData.ticker.riseFallRate > 0 ? '+' : ''}${(currentPriceData.ticker.riseFallRate * 100).toFixed(2)}%`
                    : 'N/A'
                  }
                </span>
              </div>
            )}
          </div>

          {/* Price Refresh Timer and Polling Status */}
          <div className="flex items-center space-x-4">
            {/* Polling Status Indicator */}
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${isDataFresh ? 'bg-green-500' : 'bg-yellow-500'
                }`} />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {isDataFresh ? 'Fresh' : 'Stale'}
              </span>
            </div>

            <PriceRefreshTimer compact={true} showControls={false} />
            <button
              onClick={testKlineAPI}
              className="p-2 text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
              title="Test Kline API"
            >
              🧪
            </button>
            <button
              onClick={handleRefreshChart}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Refresh chart data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Timeframe Controls */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Timeframe:</span>
            <div className="flex space-x-1">
              {TIMEFRAMES.map((timeframe) => (
                <button
                  key={timeframe.value}
                  onClick={() => handleTimeframeChange(timeframe)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${selectedTimeframe.value === timeframe.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  {timeframe.label}
                </button>
              ))}
            </div>
          </div>

          {/* Chart Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowVolume(!showVolume)}
              className={`px-3 py-1 text-xs rounded transition-colors ${showVolume
                ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
            >
              Volume
            </button>
            <button
              onClick={() => setShowIndicators(!showIndicators)}
              className={`px-3 py-1 text-xs rounded transition-colors ${showIndicators
                ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
            >
              Indicators
            </button>
          </div>
        </div>
      </div>

      {/* Chart Content */}
      <div className="p-6">
        {/* Position Summary */}
        {positions.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Active Positions:
              </span>
              <div className="flex space-x-4">
                {positionLevels.map((level) => {
                  const position = positions.find(p => p.positionId === level.positionId);
                  return (
                    <div key={level.positionId} className="text-xs">
                      <button
                        onClick={() => position && handlePositionSelect(position)}
                        className="hover:bg-gray-200 dark:hover:bg-gray-600 p-1 rounded transition-colors"
                      >
                        <span className={`font-medium ${level.positionType === 1
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                          }`}>
                          {level.positionType === 1 ? 'Long' : 'Short'} {level.size}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400 ml-2">
                          @ ${level.entryPrice.toFixed(4)}
                        </span>
                        {level.unrealizedPnL !== 0 && (
                          <span className={`ml-2 ${level.unrealizedPnL > 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                            }`}>
                            {level.unrealizedPnL > 0 ? '+' : ''}${level.unrealizedPnL.toFixed(2)}
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Quick Action Controls */}
        {showQuickActions && selectedPosition && currentPriceData && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Edit3 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Quick Actions - {selectedPosition.symbol} {selectedPosition.positionType === 1 ? 'Long' : 'Short'}
                </span>
              </div>
              <button
                onClick={() => setShowQuickActions(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Quick Stop Loss */}
              <div className="space-y-2">
                <div className="flex items-center space-x-1">
                  <Shield className="w-3 h-3 text-red-500" />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Stop Loss</span>
                </div>
                <div className="space-y-1">
                  {[0.5, 1.0, 2.0].map(percentage => {
                    const currentPrice = currentPriceData.price;
                    const slPrice = selectedPosition.positionType === 1
                      ? currentPrice * (1 - percentage / 100)  // Long: SL below current price
                      : currentPrice * (1 + percentage / 100); // Short: SL above current price

                    return (
                      <button
                        key={percentage}
                        onClick={() => handleQuickStopLoss(selectedPosition, slPrice)}
                        disabled={updatePositionTPSL.isPending}
                        className="w-full text-xs px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
                      >
                        -{percentage}% (${slPrice.toFixed(4)})
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quick Take Profit */}
              <div className="space-y-2">
                <div className="flex items-center space-x-1">
                  <Target className="w-3 h-3 text-green-500" />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Take Profit</span>
                </div>
                <div className="space-y-1">
                  {[1.0, 2.0, 5.0].map(percentage => {
                    const currentPrice = currentPriceData.price;
                    const tpPrice = selectedPosition.positionType === 1
                      ? currentPrice * (1 + percentage / 100)  // Long: TP above current price
                      : currentPrice * (1 - percentage / 100); // Short: TP below current price

                    return (
                      <button
                        key={percentage}
                        onClick={() => handleQuickTakeProfit(selectedPosition, tpPrice)}
                        disabled={updatePositionTPSL.isPending}
                        className="w-full text-xs px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors disabled:opacity-50"
                      >
                        +{percentage}% (${tpPrice.toFixed(4)})
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Chart */}
        <div style={{ height: `${height - 100}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <defs>
                <linearGradient id="priceRangeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis
                dataKey="time"
                stroke="#6B7280"
                fontSize={12}
                tick={{ fill: '#6B7280' }}
              />
              <YAxis
                domain={['dataMin - 10', 'dataMax + 10']}
                stroke="#6B7280"
                fontSize={12}
                tick={{ fill: '#6B7280' }}
                tickFormatter={(value) => `$${value.toFixed(2)}`}
              />
              <Tooltip content={<CustomTooltip />} />

              {/* Price range area (high-low) */}
              <Area
                type="monotone"
                dataKey="high"
                stroke="#6B7280"
                strokeWidth={1}
                fill="url(#priceRangeGradient)"
                fillOpacity={0.1}
                connectNulls={false}
              />
              <Area
                type="monotone"
                dataKey="low"
                stroke="#6B7280"
                strokeWidth={1}
                fill="transparent"
                connectNulls={false}
              />

              {/* Open prices line (dashed) */}
              <Line
                type="monotone"
                dataKey="open"
                stroke="#9CA3AF"
                strokeWidth={1}
                dot={false}
                connectNulls={false}
                strokeDasharray="3 3"
                strokeOpacity={0.7}
              />

              {/* Close price line (main price line) */}
              <Line
                type="monotone"
                dataKey="close"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />

              {/* Technical Indicators */}
              {showIndicators && (
                <>
                  {/* Moving Averages */}
                  <Line
                    type="monotone"
                    dataKey="sma20"
                    stroke="#F59E0B"
                    strokeWidth={1}
                    dot={false}
                    connectNulls={false}
                    name="SMA 20"
                  />
                  <Line
                    type="monotone"
                    dataKey="sma50"
                    stroke="#EF4444"
                    strokeWidth={1}
                    dot={false}
                    connectNulls={false}
                    name="SMA 50"
                  />
                  <Line
                    type="monotone"
                    dataKey="ema20"
                    stroke="#8B5CF6"
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    dot={false}
                    connectNulls={false}
                    name="EMA 20"
                  />

                  {/* Bollinger Bands */}
                  <Line
                    type="monotone"
                    dataKey="bbUpper"
                    stroke="#6B7280"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                    connectNulls={false}
                    name="BB Upper"
                  />
                  <Line
                    type="monotone"
                    dataKey="bbLower"
                    stroke="#6B7280"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    dot={false}
                    connectNulls={false}
                    name="BB Lower"
                  />
                </>
              )}

              {/* Support and Resistance Levels */}
              {showIndicators && indicators.supportResistance.slice(0, 5).map((level, index) => (
                <ReferenceLine
                  key={`sr-${index}`}
                  y={level.price}
                  stroke={level.type === 'support' ? '#10B981' : '#EF4444'}
                  strokeDasharray="2 2"
                  strokeWidth={1}
                  strokeOpacity={0.6}
                  label={{
                    value: `${level.type.toUpperCase()}: $${level.price.toFixed(4)} (${level.touches})`,
                    position: 'right',
                    fill: level.type === 'support' ? '#10B981' : '#EF4444',
                    fontSize: 9
                  }}
                />
              ))}

              {/* Position entry price lines */}
              {positionLevels.map((level) => (
                <ReferenceLine
                  key={`entry-${level.positionId}`}
                  y={level.entryPrice}
                  stroke={level.positionType === 1 ? '#10B981' : '#EF4444'}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: `Entry: $${level.entryPrice.toFixed(4)}`,
                    position: 'right',
                    fill: level.positionType === 1 ? '#10B981' : '#EF4444',
                    fontSize: 12
                  }}
                />
              ))}

              {/* Stop-loss and take-profit lines */}
              {orderLevels.map((level) => (
                <Fragment key={`orders-${level.orderId}`}>
                  {level.stopLoss && (
                    <ReferenceLine
                      y={level.stopLoss}
                      stroke="#EF4444"
                      strokeDasharray="3 3"
                      strokeWidth={1}
                      label={{
                        value: `SL: $${level.stopLoss.toFixed(4)}`,
                        position: 'right',
                        fill: '#EF4444',
                        fontSize: 10
                      }}
                    />
                  )}
                  {level.takeProfit && (
                    <ReferenceLine
                      y={level.takeProfit}
                      stroke="#10B981"
                      strokeDasharray="3 3"
                      strokeWidth={1}
                      label={{
                        value: `TP: $${level.takeProfit.toFixed(4)}`,
                        position: 'right',
                        fill: '#10B981',
                        fontSize: 10
                      }}
                    />
                  )}
                </Fragment>
              ))}

              {/* Current price line */}
              {currentPriceData && (
                <ReferenceLine
                  y={currentPriceData.price}
                  stroke="#F59E0B"
                  strokeWidth={2}
                  label={{
                    value: `Current: $${currentPriceData.price.toFixed(4)}`,
                    position: 'right',
                    fill: '#F59E0B',
                    fontSize: 12
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* RSI Indicator Chart */}
        {showIndicators && (
          <div style={{ height: '120px' }} className="mt-4">
            <div className="mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                RSI (14)
              </span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis
                  dataKey="time"
                  stroke="#6B7280"
                  fontSize={10}
                  tick={{ fill: '#6B7280' }}
                />
                <YAxis
                  domain={[0, 100]}
                  stroke="#6B7280"
                  fontSize={10}
                  tick={{ fill: '#6B7280' }}
                />
                <Line
                  type="monotone"
                  dataKey="rsi"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
                {/* RSI Overbought/Oversold Lines */}
                <ReferenceLine y={70} stroke="#EF4444" strokeDasharray="2 2" strokeWidth={1} />
                <ReferenceLine y={30} stroke="#10B981" strokeDasharray="2 2" strokeWidth={1} />
                <ReferenceLine y={50} stroke="#6B7280" strokeDasharray="1 1" strokeWidth={1} opacity={0.5} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Volume Chart */}
        {showVolume && (
          <div style={{ height: '100px' }} className="mt-4">
            <div className="mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Volume
              </span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis
                  dataKey="time"
                  stroke="#6B7280"
                  fontSize={10}
                  tick={{ fill: '#6B7280' }}
                />
                <YAxis
                  stroke="#6B7280"
                  fontSize={10}
                  tick={{ fill: '#6B7280' }}
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                />
                <Bar dataKey="volume" fill="#8B5CF6" opacity={0.6} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize the component for performance optimization
export const TradingChart = memo(TradingChartComponent, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.symbol === nextProps.symbol &&
    prevProps.height === nextProps.height &&
    prevProps.positions.length === nextProps.positions.length &&
    prevProps.orders?.length === nextProps.orders?.length &&
    // Deep comparison for positions (check if any position data changed)
    prevProps.positions.every((pos, index) => {
      const nextPos = nextProps.positions[index];
      return nextPos &&
        pos.positionId === nextPos.positionId &&
        pos.openAvgPrice === nextPos.openAvgPrice &&
        pos.holdVol === nextPos.holdVol;
    })
  );
});
